import { Injectable } from "@angular/core";
import { NotificationSocket } from "./notification-socket.types";

/**
 * Thin seam around the `socket.io-client` package — the ONLY place in the
 * notification feature that references it (mirrors
 * flymsg-roleplay's `CopilotSocketFactory`, issue #54's pattern):
 *
 * 1. Unit tests substitute a fully mocked factory and never need the real
 *    package (see `notification-socket.service.spec.ts`).
 * 2. The library is loaded via a dynamic `import()` so esbuild code-splits
 *    it into its own lazy chunk — it is only fetched for a beta user whose
 *    `NotificationBellComponent` actually mounts, never in the initial
 *    bundle for the non-beta majority (issue #416's beta gate).
 */
@Injectable({ providedIn: "root" })
export class NotificationSocketFactory {
  /**
   * Builds a (non-auto-connecting) Socket.IO client for `url`. Callers pass
   * extra `opts` (merged over the defaults) — notably the `auth` callback
   * that presents a fresh realtime JWT on every (re)connect handshake.
   */
  async create(url: string, opts: Record<string, unknown> = {}): Promise<NotificationSocket> {
    const mod = (await import("socket.io-client")) as unknown as {
      io: (address: string, opts?: Record<string, unknown>) => NotificationSocket;
    };
    // No `transports` pin, same reasoning as CopilotSocketFactory: some
    // intermediaries between the browser and the server silently drop the
    // WebSocket upgrade, and pinning to ['websocket'] leaves no fallback.
    // Socket.IO's default (polling first, transparent upgrade) already
    // prefers WebSocket whenever it's actually reachable.
    return mod.io(url, {
      autoConnect: false,
      ...opts,
    });
  }
}
